<template>
  <WdsLabel
    v-if="badge"
    v-tooltip="badge.tooltip"
    :warning="badge.tone === 'shared'"
    :primary="badge.tone === 'managed-elsewhere'"
    :data-test="`custom-field-visibility-${badge.key}`"
    subtle
    xs
    class="tw-ml-1 tw-align-middle tw-rounded-full"
  >
    <WdsIcon :name="badge.icon" :size="12" />
  </WdsLabel>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsIcon, WdsIconNameEnum, WdsLabel } from '@worksome/wds-components'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { $t } from '@/composables/translation'

interface VisibilityBadge {
  key: string
  tone: 'shared' | 'managed-elsewhere'
  icon: `${WdsIconNameEnum}`
  tooltip: string
}

const props = withDefaults(
  defineProps<{
    appliesTo: TypeSupportingCustomFieldValues
    sharedWithClients: boolean
    sharedWithStaffingAgencies: boolean
    // Resolved server-side. Fields owned by an organisation the viewer's
    // account belongs to count as the viewer's own, not managed elsewhere.
    managedElsewhere?: boolean
    ownerAccountName?: string | null
  }>(),
  { ownerAccountName: null },
)

const badge = computed<VisibilityBadge | null>(() => {
  if (props.appliesTo !== TypeSupportingCustomFieldValues.Job) {
    return null
  }

  if (props.managedElsewhere) {
    return {
      key: 'managed-elsewhere',
      tone: 'managed-elsewhere',
      icon: WdsIconNameEnum.ENTERPRISE,
      tooltip: props.ownerAccountName
        ? $t('Asked by :account', { account: props.ownerAccountName })
        : $t('Managed outside your account'),
    }
  }

  if (props.sharedWithStaffingAgencies && props.sharedWithClients) {
    return {
      key: 'shared-with-staffing-agencies-and-clients',
      tone: 'shared',
      icon: WdsIconNameEnum.SHARE,
      tooltip: $t('Visible to client companies and staffing agencies'),
    }
  }

  if (props.sharedWithStaffingAgencies) {
    return {
      key: 'shared-with-staffing-agencies',
      tone: 'shared',
      icon: WdsIconNameEnum.SHARE,
      tooltip: $t('Visible to staffing agencies'),
    }
  }

  if (props.sharedWithClients) {
    return {
      key: 'shared-with-clients',
      tone: 'shared',
      icon: WdsIconNameEnum.SHARE,
      tooltip: $t('Visible to client companies'),
    }
  }

  return null
})
</script>

<style scoped lang="scss">
.wds-label {
  @apply tw-rounded-full;
  @apply tw-p-[4px];
  @apply tw-py-[3px];
}
</style>
